export type CompetitionPhase = 'upcoming' | 'in_progress';
export type EntryOpenness = 'open' | 'closed';

export type PublicCompetitionStatusInput = {
  competition_type?: string | null;
  competitionType?: string | null;
  start_date?: string | Date | null;
  startDate?: string | Date | null;
};

export type PublicCompetitionStatusOpts = {
  /** World Cup: first GROUP_STAGE kickoff */
  wcFirstKickoff?: Date | null;
  /** LMS (and similar): earliest Gameweek 1 kickoff — entries close at this time */
  firstGameweekDeadline?: Date | null;
};

function toValidDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function hasPassed(date: Date | null): boolean {
  return !!date && Date.now() >= date.getTime();
}

/**
 * Public/available competition card status.
 *
 * Phase ("Upcoming" / "In progress"):
 * - World Cup: first match kickoff
 * - LMS / Predictor: competition start_date
 *
 * Entrants ("Open for entries" / "Closed to new entrants"):
 * - Predictor: always open
 * - World Cup: closes at first match kickoff
 * - LMS: closes at Gameweek 1 kickoff (not start_date)
 */
export function getPublicCompetitionStatus(
  competition: PublicCompetitionStatusInput,
  opts?: PublicCompetitionStatusOpts
): {
  phase: CompetitionPhase;
  entrants: EntryOpenness;
} {
  const competitionType =
    competition.competitionType ?? competition.competition_type ?? 'lms';
  const startDate = toValidDate(competition.startDate ?? competition.start_date);

  const phaseReference =
    competitionType === 'worldcup'
      ? (toValidDate(opts?.wcFirstKickoff) ?? null)
      : startDate;
  const phase: CompetitionPhase = hasPassed(phaseReference) ? 'in_progress' : 'upcoming';

  let entrants: EntryOpenness;
  if (competitionType === 'predictor') {
    entrants = 'open';
  } else if (competitionType === 'worldcup') {
    entrants = hasPassed(toValidDate(opts?.wcFirstKickoff)) ? 'closed' : 'open';
  } else {
    // LMS: open until GW1 deadline. If unknown, stay open (avoid false "closed").
    const closeDate = toValidDate(opts?.firstGameweekDeadline);
    entrants = closeDate ? (hasPassed(closeDate) ? 'closed' : 'open') : 'open';
  }

  return { phase, entrants };
}

export function publicCompetitionTypeLabel(
  competitionType: string | null | undefined
): string {
  if (competitionType === 'worldcup') return 'World Cup';
  if (competitionType === 'predictor') return 'Predictor';
  return 'Last Man Standing';
}

type CompetitionGameRow = {
  competition_id: string;
  games: { match_date: string; gameweek: number | null } | null;
};

type CompetitionDateRange = {
  id: string;
  start_date?: string | Date | null;
  startDate?: string | Date | null;
  end_date?: string | Date | null;
  endDate?: string | Date | null;
};

/**
 * Earliest kickoff of the first gameweek for each competition, matching
 * enrollment join-eligibility (games within start/end, grouped by gameweek).
 */
export function computeFirstGameweekDeadlines(
  rows: CompetitionGameRow[],
  competitions: CompetitionDateRange[]
): Record<string, Date> {
  const gamesByComp = new Map<string, { match_date: string; gameweek: number }[]>();
  for (const row of rows) {
    const game = row.games;
    if (!game?.match_date || game.gameweek == null) continue;
    const list = gamesByComp.get(row.competition_id) || [];
    list.push({ match_date: game.match_date, gameweek: game.gameweek });
    gamesByComp.set(row.competition_id, list);
  }

  const result: Record<string, Date> = {};
  for (const comp of competitions) {
    const start = toValidDate(comp.startDate ?? comp.start_date);
    const end = toValidDate(comp.endDate ?? comp.end_date);
    if (!start) continue;

    const inRange = (gamesByComp.get(comp.id) || []).filter((g) => {
      const matchDate = new Date(g.match_date);
      if (isNaN(matchDate.getTime())) return false;
      return matchDate >= start && (!end || matchDate <= end);
    });

    const earliestByWeek = new Map<number, Date>();
    for (const g of inRange) {
      const matchDate = new Date(g.match_date);
      const existing = earliestByWeek.get(g.gameweek);
      if (!existing || matchDate < existing) {
        earliestByWeek.set(g.gameweek, matchDate);
      }
    }

    const weeks = Array.from(earliestByWeek.keys()).sort((a, b) => a - b);
    if (weeks.length === 0) continue;
    const firstWeekDeadline = earliestByWeek.get(weeks[0]);
    if (firstWeekDeadline) {
      result[comp.id] = firstWeekDeadline;
    }
  }
  return result;
}
